/**
 * <sticky-buy-button> — two different bars share this element and its
 * add-to-cart logic:
 *   - .rec-sticky-buy (snippets/buy-button-sticky.liquid, rendered inside the
 *     recommendations feed section): fades in/out as the feed enters/leaves
 *     the viewport (.is-in-view). Desktop only (CSS).
 *   - .wt-product__sticky-buy (snippets/buy-button-sticky-mobile.liquid,
 *     rendered directly in main-product.liquid): fixed bottom bar, always
 *     visible. Mobile only (CSS).
 * Both variants add the selected variant to the cart and confirm with the
 * minicart. They never fall back to the first available size: if the product
 * has sizes and none is picked yet, mobile opens the size sheet and desktop
 * sends the shopper back up to the product card (see onClick).
 */
class StickyBuyButton extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('[data-add-to-cart]');
    if (!this.btn) return;

    this.isMobileBar = this.classList.contains('wt-product__sticky-buy');
    this.btn.addEventListener('click', (e) => this.onClick(e));

    if (!this.isMobileBar) {
      this.observeRecsFeed();
    }
  }

  observeRecsFeed() {
    const feed = document.querySelector('.recs-feed');
    if (feed && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        this.classList.toggle('is-in-view', entries.some((entry) => entry.isIntersecting));
      });
      this.observer.observe(feed);
    } else {
      // No feed / no observer support: keep it visible where it sits.
      this.classList.add('is-in-view');
    }
  }

  onClick(e) {
    e.preventDefault();
    const btn = this.btn;
    if (btn.hasAttribute('disabled') || btn.getAttribute('aria-disabled') === 'true') return;

    // Coming-soon / restock: send the shopper to the main form's notify UI.
    if (btn.hasAttribute('data-is-coming-soon')) {
      document.querySelector('.wt-product__add-to-cart')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // No size chosen yet on mobile: open the size selector instead of adding to cart.
    if (this.shouldShowMobileSizeModal()) {
      this.openMobileSizeModal();
      return;
    }

    // Same on desktop, where there is no bottom-sheet: the form's hidden id still
    // holds the first available variant, so adding here would silently pick the
    // smallest size. Send the shopper back up to the product card instead.
    if (this.shouldBlockForMissingSize()) {
      document.querySelector('product-form')?.handleErrorMessage?.(window.variantStrings?.selectSizeRequired);
      this.scrollToProduct();
      return;
    }

    const productForm = document.querySelector('product-form form');
    const variantId = productForm?.querySelector('input[name="id"]')?.value;

    if (!variantId) {
      document.querySelector('.wt-product__add-to-cart')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    this.addToCart(variantId);
  }

  scrollToProduct() {
    const target = document.querySelector('.brn-product') || document.querySelector('#MainContent');
    const y = target ? target.getBoundingClientRect().top + window.pageYOffset - 80 : 0;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  }

  addToCart(variantId) {
    const btn = this.btn;
    btn.setAttribute('aria-disabled', 'true');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    window.CartAddSections.attach(formData);

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    })
      .then(async (response) => {
        if (response.ok) return response.json();
        // Shopify explains the refusal (sold out, too many requests…) in a JSON
        // body; a throttled request may come back as an HTML page instead.
        const body = await response.json().catch(() => null);
        throw new Error(body?.description || body?.message || `HTTP ${response.status}`);
      })
      .then((data) => {
        // Notify cart-sync and other cart listeners.
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pdp-sticky', productVariantId: variantId, cartData: data });
        }

        this.openMinicart(data, window.CartAddSections.applyBubble(data));
        window.CartAddSections.refreshDrawer();
        // The mobile bar returns the shopper to the top of the product page; the
        // desktop one stays put — the minicart alone confirms the add.
        if (this.isMobileBar) this.scrollToProduct();
      })
      .catch((error) => {
        console.error('[sticky] add to cart failed:', error);
        // Only Shopify's own explanations are shown as-is; network failures and
        // bare status codes get the generic cart error.
        const message = error instanceof TypeError || /^HTTP \d+$/.test(error.message)
          ? window.cartStrings?.error || 'Error'
          : error.message;
        window.toast?.show(message, { type: 'error', icon: 'error' });
      })
      .finally(() => {
        btn.removeAttribute('aria-disabled');
      });
  }

  // Open the theme minicart as the "Añadido a la cesta" confirmation popup.
  openMinicart(data, itemCount) {
    if (!window.minicart || typeof window.minicart.open !== 'function') return;

    const lang = document.documentElement.lang || 'es-ES';
    const locale = lang.toLowerCase().includes('es') ? 'es-ES' : lang;
    const priceCents = data.final_price ?? data.price ?? 0;
    let priceText = '';
    try {
      priceText = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(priceCents / 100);
    } catch {
      priceText = '';
    }

    const selectedSize = data.options_with_values?.find((o) => /size|talla/i.test(o?.name || ''))?.value;
    const title = selectedSize && window.cartStrings?.addedToCartSize
      ? window.cartStrings.addedToCartSize.replace('__size__', selectedSize)
      : window.cartStrings?.addedToCart || 'Añadido a la cesta';

    window.minicart.open({
      title,
      productTitle: data.product_title,
      imageSrc: data.image,
      imageAlt: data.title,
      priceText,
      variantText: data.variant_title,
      itemCount,
    });
  }

  // Mobile size modal helper methods
  shouldShowMobileSizeModal() {
    // Check if we're on mobile (matches $br-m in scss/abstracts/_variables.scss)
    if (window.innerWidth >= 1024) return false;

    // Single "U" size: the size fieldset is hidden and nothing to pick.
    const submitButton = document.querySelector('product-form form [type="submit"]');
    if (submitButton?.hasAttribute('data-single-u-size')) return false;

    // variant-picker renders more than one <variant-options> (a mobile-only
    // color selector plus the full color+size one) — search across all of them.
    const sizeOptions = document.querySelectorAll('variant-options input[type="radio"][data-option-type="size"]');
    if (!sizeOptions.length) return false;

    // Always reopen the sheet on mobile so the shopper can change size on
    // every tap, instead of silently re-adding whichever size was picked
    // last (mirrors product-form.js's shouldShowMobileSizeModal).
    return true;
  }

  // Desktop equivalent of shouldShowMobileSizeModal (mirrors product-form.js):
  // there is no bottom-sheet, so a missing size blocks the add instead.
  shouldBlockForMissingSize() {
    if (window.innerWidth < 1024) return false;

    const submitButton = document.querySelector('product-form form [type="submit"]');
    if (submitButton?.hasAttribute('data-single-u-size')) return false;

    const sizeOptions = document.querySelectorAll('variant-options input[type="radio"][data-option-type="size"]');
    if (!sizeOptions.length) return false;

    return !Array.from(sizeOptions).some((input) => input.checked && !input.disabled);
  }

  openMobileSizeModal() {
    const modal = document.querySelector('#sheet-size');
    if (!modal) return;

    if (!window.mobileSizeModal) {
      // eslint-disable-next-line no-undef
      window.mobileSizeModal = new MobileSizeModal();
    }
    if (window.mobileSizeModal && typeof window.mobileSizeModal.open === 'function') {
      window.mobileSizeModal.open();
    }
  }
}

customElements.define('sticky-buy-button', StickyBuyButton);

// Safari iOS: trigger 1px window scroll on load so the browser chrome collapses
if (/iPhone|iPad/.test(navigator.userAgent)) {
  window.scrollTo(0, 1);
}

/**
 * Mobile PDP title: unroll a copy of it into the header on scroll.
 *
 * `.brn-product__title--mobile` (in the flow, under the breadcrumbs) is only
 * ever *measured*, never styled — it scrolls under the fixed header untouched.
 * Once its top edge passes the header's bottom edge, `.wt-header__product-title`
 * (a flex item in `.wt-header__brand`, right after the menu trigger) unrolls
 * left → right. Scrolling back up rolls it up again, right → left.
 *
 * The unroll is driven from JS with the Web Animations API rather than a CSS
 * transition/animation on `.is-visible`. Both CSS routes proved unreliable
 * here: the element flips from `display: none` to `display: block` the moment
 * the animation is activated, so a transition frequently had no painted "from"
 * value to interpolate and snapped straight to the end state. `element.animate`
 * always plays from the keyframe list it is given, whatever the cascade did.
 */
class ProductTitleScroll {
  constructor() {
    this.source = document.querySelector('.brn-product__title--mobile');
    this.target = document.querySelector('[data-pdp-header-title]');
    this.header = document.querySelector('.page-header') || document.querySelector('.wt-header');

    if (!this.source || !this.target || !this.header) return;

    this.desktop = window.matchMedia(`(min-width: ${ProductTitleScroll.BREAKPOINT}px)`);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.active = false;
    this.ticking = false;
    this.docked = false;
    this.wipe = null;

    this.target.textContent = this.source.textContent.trim();

    this.onScroll = () => this.requestUpdate();
    this.onResize = () => {
      this.syncMode();
      this.requestUpdate();
    };

    this.syncMode();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    // --header-height and web fonts land after first paint; re-measure then.
    requestAnimationFrame(() => this.requestUpdate());
    document.fonts?.ready.then(() => this.requestUpdate());
  }

  // Matches $br-m — above it both title copies are display:none.
  static BREAKPOINT = 1024;

  // Fully clipped from the right edge inwards, so the reveal runs from the menu
  // trigger outwards. Mirrors the resting value in scss/components/_header.scss.
  static CLIPPED = 'inset(0 100% 0 0)';

  static REVEALED = 'inset(0 0 0 0)';

  // Px of slack before the title is considered undocked again, so a jittery
  // scroll sitting right on the threshold can't strobe the wipe.
  static HYSTERESIS = 8;

  syncMode() {
    const shouldRun = !this.desktop.matches;
    if (shouldRun === this.active) return;

    this.active = shouldRun;
    document.documentElement.classList.toggle('brn-pdp-title-fly', shouldRun);
    if (!shouldRun) {
      this.wipe?.cancel();
      this.wipe = null;
      this.target.classList.remove('is-visible');
      this.docked = false;
    }
  }

  /**
   * Plays the unroll (or the roll-up). Cancelling the in-flight animation first
   * keeps the two directions from stacking when the user reverses mid-wipe; the
   * new one simply starts from the clip value the cancelled one left behind.
   */
  playWipe(show) {
    const { CLIPPED, REVEALED } = ProductTitleScroll;
    // A left-to-right reveal carries no vestibular risk, so reduced motion gets
    // a shortened wipe rather than none at all.
    const reduced = this.reduceMotion.matches;
    const from = show ? CLIPPED : REVEALED;
    const to = show ? REVEALED : CLIPPED;

    this.wipe?.cancel();
    // Exit (scrolling back up) is much shorter than the entrance: the in-flow title below
    // the breadcrumbs has no fade-in of its own — it's already there, unstyled, the moment
    // it scrolls back past the header edge — so the header copy needs to be gone almost as
    // fast, or both are visible at once for the difference in duration. Reduced motion only
    // shortens the entrance: the exit is already a blink, and stretching it would leave the
    // two copies overlapping for longer.
    this.wipe = this.target.animate([{ clipPath: from }, { clipPath: to }], {
      duration: show ? (reduced ? 320 : 1000) : 120,
      easing: show ? 'cubic-bezier(0.33, 1, 0.68, 1)' : 'cubic-bezier(0.32, 0, 0.67, 0)',
      fill: 'forwards'
    });
  }

  requestUpdate() {
    if (!this.active || this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.update();
    });
  }

  update() {
    const source = this.source.getBoundingClientRect();
    if (!source.width) return;

    const headerBottom = this.header.getBoundingClientRect().bottom;
    const edge = this.docked ? headerBottom + ProductTitleScroll.HYSTERESIS : headerBottom;
    const docked = source.top <= edge;
    if (docked === this.docked) return;

    this.docked = docked;
    this.target.classList.toggle('is-visible', docked);
    this.playWipe(docked);
  }
}

if (document.querySelector('.brn-product__title--mobile')) {
  new ProductTitleScroll();
}
